import { Box, Button, Flex, IconButton, Text } from '@rocket/ui';
import { useState } from 'react';
import { LuChevronLeft, LuChevronRight, LuCopy } from 'react-icons/lu';
import type { SuggestedMatch } from '../onboarding-api';

interface Props {
  matches: Array<SuggestedMatch>;
  onCopy: (ruleId: string, playbookName: string, samePlaybook?: boolean) => void;
  disabled?: boolean;
}

export function CopyMatchesCarousel({ matches, onCopy, disabled }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (matches.length === 0) {
    return null;
  }

  const current = matches[currentIndex];

  return (
    <Box borderWidth="1px" borderRadius="md" p="3" bg="blue.subtle" mb="3">
      <Flex.H align="center" justify="space-between" mb="2">
        <Flex.H align="center" gap="2">
          <LuCopy size={14} />
          <Text fontSize="xs" fontWeight="600">
            {matches.length} similar {matches.length === 1 ? 'rule' : 'rules'} found
            {matches.some((m) => m.samePlaybook) ? ' (includes this playbook)' : ''}
          </Text>
        </Flex.H>
        {matches.length > 1 && (
          <Text fontSize="xs" color="fg.muted">
            {currentIndex + 1} of {matches.length}
          </Text>
        )}
      </Flex.H>

      <Box borderWidth="1px" borderRadius="md" p="3" bg="bg.surface">
        <Flex.V gap="2">
          <Text fontSize="sm" fontWeight="600">
            {current.name}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            from <strong>{current.playbookName}</strong>
            {' \u2022 '}
            {Math.round(current.confidence * 100)}% match
          </Text>
          {current.description && (
            <Text fontSize="xs" color="fg.muted">
              {current.description}
            </Text>
          )}

          {current.sampleEdit && (
            <Box
              borderWidth="1px"
              borderRadius="sm"
              p="2"
              bg="bg.muted"
              fontSize="xs"
              fontFamily="mono"
            >
              <Text color="red.fg" mb="1">
                <strong>Before:</strong> {current.sampleEdit.before}
              </Text>
              <Text color="green.fg">
                <strong>After:</strong> {current.sampleEdit.after}
              </Text>
            </Box>
          )}

          <Button
            size="xs"
            colorPalette={current.samePlaybook ? 'orange' : 'blue'}
            disabled={disabled}
            onClick={() => onCopy(current.ruleId, current.playbookName, current.samePlaybook)}
          >
            {current.samePlaybook ? 'Add Training to Existing Rule' : 'Use This Instead'}
          </Button>
        </Flex.V>
      </Box>

      {matches.length > 1 && (
        <Flex.H justify="center" gap="2" mt="2">
          <IconButton
            size="xs"
            variant="ghost"
            onClick={() => setCurrentIndex((i) => i - 1)}
            disabled={currentIndex === 0}
            aria-label="Previous match"
          >
            <LuChevronLeft size={12} />
          </IconButton>
          <Flex.H gap="1" align="center">
            {matches.map((_, idx) => (
              <Box
                key={idx}
                w="1.5"
                h="1.5"
                borderRadius="full"
                bg={idx === currentIndex ? 'blue.solid' : 'gray.muted'}
                cursor="pointer"
                onClick={() => setCurrentIndex(idx)}
              />
            ))}
          </Flex.H>
          <IconButton
            size="xs"
            variant="ghost"
            onClick={() => setCurrentIndex((i) => i + 1)}
            disabled={currentIndex === matches.length - 1}
            aria-label="Next match"
          >
            <LuChevronRight size={12} />
          </IconButton>
        </Flex.H>
      )}
    </Box>
  );
}
